import React, { ReactNode } from 'react';
import { inject, observer } from 'mobx-react';
import { OperateChecker } from 'apps/components';
import { useCrumbItemsLoading } from 'hooks';
import { Cube, Folder, Factory } from 'icons';
import { clientStore } from 'apps/client/app';
import { ChangePortalBanner } from 'apps/client/components/ChangePortalBanner';
import { IconWrapper } from 'components/Tab/tab.styled';
import { Button, SvgIcon, Tooltip, VisibilityWrapper } from 'components';
import { Link, routerStore } from 'core/routing';
import { useHeaderStore, useRouterStore } from 'apps/hooks';
import Search from 'apps/components/header/blocks/search';
import { insertDivider } from 'utils/element';
import {
  sharedWithMeRoutes,
  getSystemCondition,
  routesWithViewType,
} from 'apps/routes/utils';

import { routesWithoutChatButtons } from 'constants/global';
import { frameStore } from '../../appManager';
import {
  MakeButtonContainer,
  DownloadButton,
  CollaboratorsContainer,
  DiffButtonContainer,
  DiscussButton,
} from './HeaderFileFeatures';
import { ProjectSortBy } from './ProjectSorter';
import { ViewToggle } from './ViewToggle';

const NewButton = observer(
  ({
    text,
    icon,
    onClick,
    dataTestId,
    disabled,
    dataFor,
    dataTip,
  }: {
    text: ReactNode;
    icon: ReactNode;
    onClick: (event: any) => void;
    disabled?: boolean;
    dataTestId: string;
    dataFor?: string;
    dataTip?: string;
  }) => {
    return (
      <OperateChecker type='canCreateIn'>
        <div
          data-for={dataFor}
          data-tip={dataTip}
          className={`${
            disabled ? 'opacity-50' : 'hover:opacity-70'
          } w-full h-full items-center justify-center`}
        >
          <Link>
            <div
              className={`flex items-center ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } justify-center pl-2`}
              data-testid={dataTestId}
              onClick={e => {
                if (!disabled) {
                  onClick(e);
                }
              }}
            >
              <p className='text-sm mr-1 font-semibold'>{text}</p>
              <IconWrapper style={{ margin: 0 }}>
                <SvgIcon color='primary' size='small' icon={icon} />
              </IconWrapper>
            </div>
          </Link>
        </div>
      </OperateChecker>
    );
  },
);

type SubHeaderProps = {
  lefts?: ReactNode[];
  rights?: ReactNode[];
  className?: string;
  notificationCenter?: { MarkAllNotificationsAsRead: () => void };
};

export const SubHeaderActions = inject('notificationCenter')(
  observer(({ rights, notificationCenter }: SubHeaderProps) => {
    const disabled =
      !clientStore.isBusinessPlanOfCurrentSpace &&
      CI === 'false' &&
      !clientStore.hasRole('INTERMEDIATOR');

    const NewRFQ = (
      <>
        <NewButton
          text='New RFQ'
          dataTip='Upgrade to Business'
          dataFor='make-part-tip'
          disabled={disabled}
          icon={<Factory />}
          onClick={() =>
            routerStore.navigate('workspace.make-part', {
              spacePath: clientStore.slugFromRouteOrSpace,
            })
          }
          dataTestId='new-rfq-button'
        />
        {disabled && (
          <Tooltip id='make-part-tip' delayShow={200} place='bottom' />
        )}
      </>
    );

    const { route } = useRouterStore();

    const headerStore = useHeaderStore();

    const { name: routeName, params } = route;
    const { spacePath, diff } = params;

    const {
      contextManager,
      sharedFileManager,
      file,
      project,
      projectSortInfo,
    } = frameStore;

    let projectsConn = null;
    if (projectSortInfo) {
      projectsConn = projectSortInfo;
    }

    const usersActivity = contextManager?.usersActivity;
    const { isRestricted } = sharedFileManager?.sharedFileQuery?.result || {};
    const withoutRestrictedProject = !project?.isRestricted && !isRestricted;

    const viewer = contextManager?.viewerControl?.viewer;
    const camera = viewer?.settings?.CurrentCamera();
    const isDiffing = camera && viewer?.isDiffing;

    const {
      isMakePartRoute,
      isEffectivePlan,
      showSearchBar,
      shouldShowFrameSideBar,
      shouldShowCADDownloadBtn,
      isInNotificationPage,
      shouldShowIntermediatorSwitcher,
    } = getSystemCondition();

    const hideShareSettings =
      !usersActivity || !sharedFileManager || !file || isMakePartRoute;

    const { crumbItemsLoading } = useCrumbItemsLoading();

    const rightAddButtonMap: Record<string, ReactNode> = {
      'workspace.spaceFiles': (
        <VisibilityWrapper visible={isEffectivePlan}>
          <NewButton
            text='Upload File'
            icon={<Cube />}
            onClick={() => {
              routerStore.navigate('workspace.share-file', {
                spacePath,
              });
            }}
            dataTestId='share-file-upload-file'
          />
        </VisibilityWrapper>
      ),
      'workspace.spaceProjects': (
        <VisibilityWrapper visible={isEffectivePlan}>
          <NewButton
            text='New Project'
            icon={<Folder />}
            onClick={() => {
              routerStore.navigate('home.newProject');
            }}
            dataTestId='ProjectPage-newProject-button'
          />
        </VisibilityWrapper>
      ),
      'workspace.rfqs': NewRFQ,
      'workspace.intermediator.rfqs': NewRFQ,
      'workspace.quotes': NewRFQ,
      'workspace.intermediator.quotes': NewRFQ,
      'workspace.orders': NewRFQ,
      'workspace.intermediator.orders': NewRFQ,
    };

    const createBtn =
      !sharedWithMeRoutes.includes(routeName) && rightAddButtonMap[routeName];

    const notInDiffPage = !diff && !isDiffing;

    const { sortOptions } = projectsConn || {};

    // share with me no sort by on projects page.
    const showProjectSortBy =
      false && // disabled
      sortOptions?.length &&
      routeName !== 'workspace.sharedWithProjects';

    const shouldShowDiscussBtn =
      !routesWithoutChatButtons.includes(route.name) &&
      file &&
      !isDiffing &&
      withoutRestrictedProject;

    const openDiffPanel = () => {
      const panelParamValue = 'diff';
      const newParams = { ...params };

      if (newParams.panel === panelParamValue) delete newParams.panel;
      else newParams.panel = panelParamValue;
      routerStore.navigate(routeName, newParams, { replace: true });
    };

    const shouldShowViewType =
      frameStore.viewType && routesWithViewType.includes(route.name);

    const rightContent = insertDivider(
      [
        ...(rights || []),
        shouldShowIntermediatorSwitcher && (
          <ChangePortalBanner
            key='ChangePortalBanner'
            className='px-2 min-w-fit'
          />
        ),
        shouldShowCADDownloadBtn && notInDiffPage && (
          <DownloadButton key='DownloadButton' />
        ),
        !hideShareSettings && notInDiffPage && (
          <CollaboratorsContainer key='sharedFileSettings' />
        ),
        shouldShowDiscussBtn && notInDiffPage && (
          <DiscussButton key='DiscussButton' />
        ),
        file && notInDiffPage && (
          <MakeButtonContainer key='MakeButtonContainer' />
        ),
        file && isDiffing && (
          <DiffButtonContainer
            onClick={openDiffPanel}
            key='DiffButtonContainer'
          />
        ),
        shouldShowViewType && <ViewToggle key='ViewToggle' />,
        showProjectSortBy && <ProjectSortBy projectsConn={projectsConn} />,
        shouldShowFrameSideBar && (
          <React.Fragment key='createButton'>{createBtn}</React.Fragment>
        ),
        isInNotificationPage && (
          <Button
            key='markAllReadButton'
            text='Mark all as Read'
            size='small'
            style={{
              height: 26,
              padding: '5px 11px 2px',
              marginRight: 8,
              marginBottom: 2,
            }}
            onClick={() => notificationCenter?.MarkAllNotificationsAsRead()}
            outline
          />
        ),
        showSearchBar && notInDiffPage && !crumbItemsLoading && (
          <Search
            key='Search'
            active
            inverted
            size={16}
            isMobile={false}
            isDark={false}
            searchFormStyle={{
              border: 'none',
            }}
            searchInputStyle={{
              height: 'inherit',
            }}
            isLogged={clientStore.signedInProfile}
            searchTerm={headerStore?.searchTerm}
          />
        ),
      ],
      { fromFirst: true },
    );

    const hasRightsContent = !!rightContent.length;

    if (!hasRightsContent) return null;

    return (
      <div data-testid='subheader-rightSection' className='flex items-center'>
        {rightContent}
      </div>
    );
  }),
);
